import { themeGet } from '@styled-system/theme-get';
import flow from 'lodash/flow';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { rem } from 'polished';
import { style } from 'styled-system';
/**
 * Creates a simple mapping that connects values of a component property to
 * provided css property
 *
 * @param from {string} - component property
 * @param to {string} - CSS property
 */

const mapStyle = (from, to) => style({
  cssProperty: to,
  prop: from
});
/**
 * Returns one of the two provided styles based on the given property value.
 *
 * For instance.
 *
 * Given the following function definition:
 *
 *   const block = selectStyleForProp('block')(
 *    css`width: 100%;`,
 *    css`width: auto;`
 *   )
 *
 * Now, "block" function can be used within a styled component style definition.
 *
 * And, if a component has "block" property set to true, then the first style of the
 * provided two will be used. If value of the property is false, then the second style
 * is used.
 *
 * Example: <MyComponent block />
 */


const selectStyleForProp = prop => (first = null, second = null) => props => props[prop] ? first : second;
/**
 * Finds a style from the given map of styles for the provided property name.
 *
 * For instance.
 *
 * Given the following function definition:
 *
 *   const size = selectStyleForPropKey('size')(
 *     {
 *        small: css`width: 20px`,
 *        regular: css`width: 30px`
 *     }
 *   )
 *
 * Now, "size" function can be used within a styled component style definition.
 *
 * And, if a component has a value for "size" property, appropriate style is then
 * looked up in the provided map of styles and returned.
 *
 * Example: <MyComponent size="small" />
 */


const selectStyleForPropKey = prop => styles => props => styles[props[prop]] || null;

const px = val => `${val}px`;

const themeGetRem = path => flow(themeGet(path), rem);

const themeGetPx = path => flow(themeGet(path), px);
/**
 * Find a given property from a scale on the theme or from a component's predefined variants by key
 *
 * Usage example:
 *
 * const getVariantProperty = themeableProperty('buttons', {
 *  default: {
 *    bg: red;
 *  }
 * })
 *
 * returns a function which takes a key, representing the property on the theme scale or component variant to use for the value
 * and an optional fallback value to be used as a default in the case that this is not set by the variant
 *
 * const styledButton = styled.button `
 *  background: getVariantProperty('bg', 'black')
 * `
 * In this case, if the 'default' variant in the theme specifies the 'bg' prop, we use that value, otherwise we use 'red' from the component default variant
 *
 * If the value is returned from the theme, it can either be:
 * - a string/number/array value
 * - a function which is invoked with a theme and returns a property from the given theme
 *
 * @param scale same purpose as the 'scale' in the styled system 'variant' function, the name of the property on the theme which contains the variants
 * @param variants variants defined on the component
 * @param key the key on the scale or the component variant that contains the target value. Can also use a string representing nested properties e.g hover.bg
 * @param fallback the default value to use if this key does not exist on the variant on neither the scale variant nor the component variant
 */


const themeableProperty = (scale, variants = {}) => (key, fallback) => props => {
  var _a;

  const {
    variant
  } = props;
  const valueFromTheme = themeGet(`${scale}.${variant}.${key}`)(props);

  if (!isNil(valueFromTheme)) {
    return typeof valueFromTheme === 'function' ? valueFromTheme(props.theme) : valueFromTheme;
  }

  return (_a = get(variants, `${variant}.${key}`)) !== null && _a !== void 0 ? _a : fallback;
};

const align = mapStyle('align', 'textAlign');
export { align, mapStyle, selectStyleForProp, selectStyleForPropKey, themeGetPx, themeGetRem, themeableProperty };
//# sourceMappingURL=index.js.map
